
import { useRef, useEffect, useState } from 'react';

interface UseMobileNavigationProps {
  isOpen: boolean;
  onClose: () => void;
}

export const useMobileNavigation = ({ isOpen, onClose }: UseMobileNavigationProps) => {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [panelWidth, setPanelWidth] = useState('80vw');
  const [expanded, setExpanded] = useState<{ [key: string]: boolean }>({ "Homepage": true });

  // Full-screen panel for mobile devices
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      
      if (width >= 768 && width < 1024) {
        // Tablet: Use fixed width up to 400px
        setPanelWidth(Math.min(400, width * 0.5) + 'px');
      } else if (width < 768) {
        // Mobile: Use full screen width for better visibility
        setPanelWidth('100vw');
      } else {
        // Desktop fallback
        setPanelWidth('400px');
      }
    };
    
    // Use passive listener for better performance
    window.addEventListener('resize', handleResize, { passive: true });
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Accessibility: focus trap in mobile menu
  useEffect(() => {
    if (!isOpen || !menuRef.current) return;
    const firstFocusable = menuRef.current.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    firstFocusable?.focus();
    const handleTab = (e: KeyboardEvent) => {
      if (!menuRef.current) return;
      const focusables = menuRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.key === 'Tab') {
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleTab);
    return () => document.removeEventListener('keydown', handleTab);
  }, [isOpen, onClose]);
  // Lock background scrolling while menu is open
  useEffect(() => {
    if (!isOpen) return;
    const originalOverflow = document.body.style.overflow;
    const originalTouchAction = document.documentElement.style.touchAction as string;

    document.body.style.overflow = 'hidden';
    document.documentElement.style.touchAction = 'none';

    return () => {
      document.body.style.overflow = originalOverflow;
      document.documentElement.style.touchAction = originalTouchAction || '';
    };
  }, [isOpen]);

  // Simplified viewport height handling
  useEffect(() => {
    if (!isOpen) return;

    const handleViewportChange = () => {
      // Force re-render of mobile navigation to recalculate heights
      if (menuRef.current) {
        const height = window.innerHeight;
        menuRef.current.style.height = `${height}px`;
      }
    };

    // Set initial height
    handleViewportChange();
    
    // Listen for viewport changes
    window.addEventListener('resize', handleViewportChange, { passive: true });
    window.addEventListener('orientationchange', handleViewportChange);
    
    // iOS Safari specific handling
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportChange);
    }

    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('orientationchange', handleViewportChange);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleViewportChange);
      }
    };
  }, [isOpen]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const toggleExpand = (label: string) => {
    setExpanded((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };
  return {
    menuRef,
    panelWidth,
    expanded,
    handleOverlayClick,
    toggleExpand,
  };
};
